"use client";

import { useState } from "react";
import clsx from "clsx";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { FgDialog } from "@/components/fg-dialog";
import { FgButton } from "@/components/fg-button";

/**
 * AI assist button — the universal "let Gemini finish this for me" affordance.
 *
 * Two-stage flow:
 *   1. User clicks the button → POST /api/ai/assist with the current context.
 *   2. The result lands in a confirmation dialog showing the proposed content
 *      + Apply / Discard buttons. Apply calls `onApply(content)` and the parent
 *      state replaces whatever the admin had (preserves the schema).
 *
 * Falls back gracefully when the backend isn't configured (returns `{ content: [] }`)
 * by showing an inline "AI unavailable" message.
 *
 * Cost guard: backend enforces 30 calls / admin / hour. The button stays
 * enabled even after a 429 so admins can retry once the cap rolls over.
 */
export interface AiAssistButtonProps {
  /** What kind of content to generate — drives the prompt template. */
  kind:
    | "choices"
    | "variants"
    | "pairs"
    | "tokens"
    | "rubric"
    | "essay_prompt"
    | "transcription";
  /** The current prompt / question text — primary input to the AI. */
  prompt: string;
  /** CEFR level — narrows vocabulary / grammar in the response. */
  level: "A1" | "A2" | "B1" | "B2" | "C1";
  /** Extra context passed through verbatim (e.g. the canonical answer). */
  extras?: Record<string, unknown>;
  /** Apply the result. Receives the structured `content` from the backend. */
  onApply: (content: unknown) => void;
  /** Custom label + size. */
  label?: string;
  size?: "xs" | "sm" | "md";
  /** Disables the button + shows a "coming soon" tooltip via aria-disabled. */
  disabled?: boolean;
  /** Optional helper shown below the button. */
  hint?: string;
}

export function AiAssistButton({
  kind,
  prompt,
  level,
  extras,
  onApply,
  label = "Generate with AI",
  size = "xs",
  disabled,
  hint,
}: AiAssistButtonProps) {
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [result, setResult] = useState<unknown | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (!prompt.trim() || busy || disabled) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post("/api/ai/assist", { kind, prompt, level, extras });
      const content = (res.data as { content?: unknown }).content;
      if (content === undefined || content === null) {
        setError("AI returned no suggestions.");
        return;
      }
      setResult(content);
      setPreviewOpen(true);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message ?? err.message ?? "AI assist failed");
    } finally {
      setBusy(false);
    }
  }

  function apply() {
    if (result === null) return;
    onApply(result);
    setPreviewOpen(false);
    setResult(null);
  }

  return (
    <>
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          onClick={run}
          disabled={disabled || busy || !prompt.trim()}
          aria-disabled={disabled || busy || !prompt.trim()}
          className={clsx(
            "inline-flex items-center gap-1.5 rounded-md font-bold uppercase tracking-wider transition-all",
            size === "xs" && "px-2 py-1 text-[10px]",
            size === "sm" && "px-3 py-1.5 text-xs",
            size === "md" && "px-3.5 py-2 text-xs",
            "bg-[#FFD60A]/15 text-[#FFD60A] border border-[#FFD60A]/30",
            "hover:bg-[#FFD60A]/25 hover:border-[#FFD60A]/50",
            "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#FFD60A]/15 disabled:hover:border-[#FFD60A]/30",
          )}
          title={disabled ? "AI assist is disabled in this build" : undefined}
        >
          {busy ? (
            <Loader2 size={size === "xs" ? 10 : 12} className="animate-spin" />
          ) : (
            <Sparkles size={size === "xs" ? 10 : 12} />
          )}
          {label}
        </button>
        {hint && !error && (
          <span className="text-[10px] font-mono text-[#8E8E93]">{hint}</span>
        )}
        {error && (
          <span className="text-[10px] font-mono text-[#FF453A]">{error}</span>
        )}
      </div>

      <FgDialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setResult(null);
        }}
        title="Apply AI suggestion?"
        description="Review the generated content, then apply it. You can edit further after applying."
        size="md"
        tone="default"
        footer={
          <>
            <FgButton variant="ghost" onClick={() => setPreviewOpen(false)}>
              Discard
            </FgButton>
            <FgButton variant="primary" onClick={apply} leftIcon={<CheckCircle2 size={14} />}>
              Apply
            </FgButton>
          </>
        }
      >
        <AssistPreview kind={kind} content={result} />
      </FgDialog>
    </>
  );
}

/**
 * Renders the AI result in a kind-appropriate preview. The output shape
 * varies per kind — we format each so it's human-readable.
 */
function AssistPreview({ kind, content }: { kind: AiAssistButtonProps["kind"]; content: unknown }) {
  if (content === null) return null;

  switch (kind) {
    case "choices":
    case "variants":
      return (
        <ul className="flex flex-col gap-1.5">
          {(content as string[]).map((s, i) => (
            <li
              key={i}
              className="rounded-md bg-[#141414] border border-[rgba(255,255,255,0.08)] px-3 py-2 text-sm text-[#FAFAFA]"
            >
              <span className="font-mono text-[10px] text-[#8E8E93] mr-2">{i + 1}.</span>
              {s}
            </li>
          ))}
        </ul>
      );
    case "pairs": {
      const pairs = content as Array<{ de: string; en: string }>;
      return (
        <ul className="flex flex-col gap-1.5">
          {pairs.map((p, i) => (
            <li
              key={i}
              className="rounded-md bg-[#141414] border border-[rgba(255,255,255,0.08)] px-3 py-2 text-sm flex items-center justify-between gap-3"
            >
              <span className="text-[#FAFAFA] font-bold">{p.de}</span>
              <span className="text-[#8E8E93]">↔</span>
              <span className="text-[#FAFAFA]">{p.en}</span>
            </li>
          ))}
        </ul>
      );
    }
    case "tokens": {
      const tokens = content as string[];
      return (
        <div className="flex flex-wrap gap-1.5">
          {tokens.map((t, i) => (
            <span
              key={i}
              className="rounded-md bg-[#1C1C1E] border border-[rgba(255,255,255,0.08)] px-2.5 py-1 text-sm font-mono text-[#FAFAFA]"
            >
              {t}
            </span>
          ))}
        </div>
      );
    }
    case "rubric": {
      const rubric = content as Array<{ criterion: string; weight: number }>;
      return (
        <ul className="flex flex-col gap-1.5">
          {rubric.map((r, i) => (
            <li
              key={i}
              className="rounded-md bg-[#141414] border border-[rgba(255,255,255,0.08)] px-3 py-2 text-sm flex items-center justify-between"
            >
              <span className="text-[#FAFAFA]">{r.criterion}</span>
              <span className="font-mono text-[#FFD60A]">{r.weight}%</span>
            </li>
          ))}
          <li className="text-[10px] font-mono text-[#8E8E93] text-right pt-1">
            Total: {rubric.reduce((s, r) => s + r.weight, 0)}
          </li>
        </ul>
      );
    }
    case "essay_prompt":
      return (
        <div className="rounded-md bg-[#141414] border border-[rgba(255,255,255,0.08)] p-3 text-sm text-[#FAFAFA] whitespace-pre-wrap">
          {String(content)}
        </div>
      );
    case "transcription":
      return (
        <div className="rounded-md bg-[#141414] border border-[rgba(255,255,255,0.08)] p-3 text-sm text-[#FAFAFA] italic">
          “{String(content)}”
        </div>
      );
    default:
      return (
        <pre className="text-xs font-mono text-[#FAFAFA] whitespace-pre-wrap">
          {JSON.stringify(content, null, 2)}
        </pre>
      );
  }
}